import { useCallback } from 'react';

import { ApplicationAnswerProps } from '@/models/Application';
import { ApplicationTemplateQuestionProps } from '@/models/ApplicationTemplate';
import { AttachmentProps } from '@/models/Attachment';
import { EventProps } from '@/models/Event';
import { EventRelationshipProps } from '@/models/EventRelationshipProps';
import { QuestionProps, SignatureQuestionResponse } from '@/models/Question';

type SaveValue = string | number | Array<string | number> | null;

type UsePreviewAnswerArgs = {
  isPreviewMode: boolean;
  fetchedAnswers: Partial<Record<number, ApplicationAnswerProps>> | null;
  fetchedQuestions: ApplicationTemplateQuestionProps | null;
  fetchedApplicationId: number | null | undefined;
  updateAnswers: (answer: ApplicationAnswerProps) => void;
};

type AddPreviewAttachmentArgs = {
  questionId: number;
  attachment: AttachmentProps;
  completionValue?: string | null;
};

type UsePreviewAnswerApi = {
  savePreviewAnswerByQuestionId: (
    questionId: number,
    answer: SaveValue,
    dataType?: string,
    questionTypeOverride?: string
  ) => Promise<ApplicationAnswerProps | null>;
  savePreviewAnswerByQuestion: (
    question: QuestionProps,
    answer: SaveValue
  ) => Promise<ApplicationAnswerProps | null>;
  addPreviewAttachmentToAnswer: (
    args: AddPreviewAttachmentArgs
  ) => Promise<ApplicationAnswerProps | null>;
  removePreviewAttachmentById: (attachmentId: number) => void;
  createPreviewEventSaveResponse: (eventInfo: EventProps) => { id: number } | null;
  createPreviewEventRelationshipResponse: (
    eventRelationshipInfo: EventRelationshipProps[]
  ) => EventRelationshipProps[] | null;
  createPreviewSignatureResponse: (
    signatureInfo: SignatureQuestionResponse
  ) => SignatureQuestionResponse | null;
};

export default function usePreviewAnswer({
  isPreviewMode,
  fetchedAnswers,
  fetchedQuestions,
  fetchedApplicationId,
  updateAnswers,
}: UsePreviewAnswerArgs): UsePreviewAnswerApi {
  const createPreviewAnswer = useCallback(
    (
      questionId: number,
      questionType: string,
      dataType: string | undefined,
      answer: SaveValue
    ): ApplicationAnswerProps => {
      const existingAnswer = fetchedAnswers?.[questionId];
      const now = new Date().toISOString();
      const normalizedType = (questionType ?? '').trim().toLowerCase();

      const choiceValues: Array<string | number> = Array.isArray(answer)
        ? answer
        : ['single select'].includes(normalizedType) && answer != null
          ? [answer]
          : [];

      let responseInt: number | null = null;
      let responseDecimal: number | null = null;
      let responseDate: string | null = null;
      let responseString: string | null = null;

      if (normalizedType === 'checkbox') {
        responseInt = typeof answer === 'number' ? answer : answer == null ? null : Number(answer);
        responseString = responseInt == null ? null : String(responseInt);
      } else if (
        ['single select', 'multicheck', 'multiselect', 'multiselect-3'].includes(normalizedType)
      ) {
        // Choice-based questions persist selected choice IDs in the choices array.
      } else if (normalizedType === 'date' || dataType === 'date') {
        responseDate = typeof answer === 'string' ? answer : null;
      } else if (normalizedType === 'year') {
        responseInt = typeof answer === 'number' ? answer : null;
      } else if (
        ['money', 'percent', 'per capita', 'total'].includes(normalizedType) ||
        ['decimal', 'double', 'float', 'money', 'percent'].includes(String(dataType).toLowerCase())
      ) {
        responseDecimal = typeof answer === 'number' ? answer : null;
      } else if (
        ['integer', 'hours', 'population'].includes(normalizedType) ||
        ['int', 'integer', 'number'].includes(String(dataType).toLowerCase())
      ) {
        responseInt = typeof answer === 'number' ? answer : null;
      } else if (typeof answer === 'string' || answer == null) {
        responseString = answer;
      }

      return {
        questionId,
        autopopulate: false,
        includeInTotal: false,
        questionType,
        datePickerRangeId: null,
        responseInt,
        responseDecimal,
        responseDate,
        responseFile: null,
        responseString,
        responseString_foreign: null,
        responseSignature: null,
        choices: choiceValues.map((choice) => ({
          applicationId: fetchedApplicationId ?? -1,
          choiceId: Number(choice),
          questionId,
        })),
        reviews: existingAnswer?.reviews ?? [],
        files: existingAnswer?.files ?? [],
        dateModified: now,
        dateCreated: existingAnswer?.dateCreated ?? now,
        modifiedBy: existingAnswer?.modifiedBy ?? ({} as never),
        createdBy: existingAnswer?.createdBy ?? ({} as never),
      };
    },
    [fetchedAnswers, fetchedApplicationId]
  );

  const savePreviewAnswerByQuestionId = useCallback(
    async (
      questionId: number,
      answer: SaveValue,
      dataType?: string,
      questionTypeOverride?: string
    ): Promise<ApplicationAnswerProps | null> => {
      if (!isPreviewMode) return null;

      const previewQuestion = fetchedQuestions?.standards
        ?.flatMap((standard) => standard.questions)
        .find((question) => question.id === questionId);

      const questionType = questionTypeOverride ?? previewQuestion?.questionType ?? 'text';
      const previewAnswer = createPreviewAnswer(questionId, questionType, dataType, answer);
      updateAnswers(previewAnswer);

      return previewAnswer;
    },
    [createPreviewAnswer, fetchedQuestions?.standards, isPreviewMode, updateAnswers]
  );

  const savePreviewAnswerByQuestion = useCallback(
    async (question: QuestionProps, answer: SaveValue): Promise<ApplicationAnswerProps | null> => {
      return savePreviewAnswerByQuestionId(
        question.id,
        answer,
        question.dataType,
        question.questionType
      );
    },
    [savePreviewAnswerByQuestionId]
  );

  const addPreviewAttachmentToAnswer = useCallback(
    async ({ questionId, attachment, completionValue = 'completed' }: AddPreviewAttachmentArgs) => {
      if (!isPreviewMode) return null;

      const baseAnswer = await savePreviewAnswerByQuestionId(questionId, completionValue);
      if (baseAnswer == null) return null;

      const existingFiles = fetchedAnswers?.[questionId]?.files ?? baseAnswer.files ?? [];
      const updatedAnswer: ApplicationAnswerProps = {
        ...baseAnswer,
        files: [...existingFiles, attachment],
      };

      updateAnswers(updatedAnswer);
      return updatedAnswer;
    },
    [fetchedAnswers, isPreviewMode, savePreviewAnswerByQuestionId, updateAnswers]
  );

  const removePreviewAttachmentById = useCallback(
    (attachmentId: number): void => {
      if (!isPreviewMode) return;

      Object.values(fetchedAnswers ?? {}).forEach((answer) => {
        if (answer == null) return;

        const nextFiles = (answer.files ?? []).filter((file) => file.id !== attachmentId);
        if (nextFiles.length !== (answer.files ?? []).length) {
          updateAnswers({ ...answer, files: nextFiles });
        }
      });
    },
    [fetchedAnswers, isPreviewMode, updateAnswers]
  );

  const createPreviewEventSaveResponse = useCallback(
    (eventInfo: EventProps): { id: number } | null => {
      if (!isPreviewMode) return null;

      return { id: eventInfo.id ?? Math.abs(Date.now() + Math.floor(Math.random() * 1000)) };
    },
    [isPreviewMode]
  );

  const createPreviewEventRelationshipResponse = useCallback(
    (eventRelationshipInfo: EventRelationshipProps[]): EventRelationshipProps[] | null => {
      if (!isPreviewMode) return null;
      return eventRelationshipInfo;
    },
    [isPreviewMode]
  );

  const createPreviewSignatureResponse = useCallback(
    (signatureInfo: SignatureQuestionResponse): SignatureQuestionResponse | null => {
      if (!isPreviewMode) return null;

      const previewRelationshipId =
        signatureInfo.signatureApplicationRelationshipId ?? Math.abs(Date.now());

      return {
        ...signatureInfo,
        signatureApplicationRelationshipId: previewRelationshipId,
        signatureId: signatureInfo.signatureId ?? previewRelationshipId,
      };
    },
    [isPreviewMode]
  );

  return {
    savePreviewAnswerByQuestionId,
    savePreviewAnswerByQuestion,
    addPreviewAttachmentToAnswer,
    removePreviewAttachmentById,
    createPreviewEventSaveResponse,
    createPreviewEventRelationshipResponse,
    createPreviewSignatureResponse,
  };
}
